import { useState, useMemo } from "react";
import debounce from "lodash.debounce";
import countries from "world-countries";
import Flag from "react-world-flags";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  List,
  ListItemButton,
  Button,
} from "src/components";
import CloseIcon from "@mui/icons-material/Close";

// Top 20 most common countries (you can tweak this list)
const PRIORITY_COUNTRIES = [
  "US", "GB", "CA", "AU", "DE", "FR", "ES", "IT", "NL", "SE",
  "NO", "DK", "CH", "BE", "AT", "PL", "PT", "IE", "FI", "NZ"
];

export default function CountrySelector({ onClose, onSelect, selected }) {
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  // Debounced search value
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
      }, 150),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value); // for instant UI feedback
    debouncedSearch(value);
  };

  // Main logic: what countries to show
  const displayedCountries = useMemo(() => {
    const lower = search.toLowerCase().trim();

    if (lower === "" && !showAll) {
      // No search + not showing all → priority + first 40 others
      const priority = PRIORITY_COUNTRIES
        .map(code => countries.find(c => c.cca2 === code))
        .filter(Boolean);

      const others = countries
        .filter(c => !PRIORITY_COUNTRIES.includes(c.cca2))
        .sort((a, b) => a.name.common.localeCompare(b.name.common))
        .slice(0, 40);

      return [...priority, ...others];
    }

    // Searching OR showAll → filter all countries
    return countries
      .filter(country => {
        if (showAll && lower === "") return true;
        return (
          country.name.common.toLowerCase().includes(lower) ||
          country.name.official.toLowerCase().includes(lower) ||
          country.cca2.toLowerCase().includes(lower)
        );
      })
      .sort((a, b) => a.name.common.localeCompare(b.name.common));

  }, [search, showAll]);

  const hasMore = search === "" && !showAll;

  return (
    <Box sx={{ width: "100%", height: "100%", bgcolor: "background.paper", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pl: 2, pr: 2, pb: 1 }}>
        <Typography variant="h6">Select Country</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      {/* Search */}
      <Box sx={{ px: 2, pb: 1 }}>
        <TextField
          fullWidth
          placeholder="Search countries..."
          size="small"
          autoFocus
          value={search}
          onChange={handleSearchChange}
          sx={{ bgcolor: "white" }}
        />
      </Box>

      {/* Country List */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <List>
          {displayedCountries.map((country) => (
            <ListItemButton
              key={country.cca2}
              selected={selected === country.cca2}
              onClick={() => {
                onSelect(country.cca2);
                onClose();
              }}
            >
              <Flag
                code={country.cca2}
                style={{ width: 28, height: 20, marginRight: 12, borderRadius: 4 }}
              />
              <Typography variant="body1">{country.name.common}</Typography>
            </ListItemButton>
          ))}
        </List>

        {/* Show "Load all" only when not searching */}
        {hasMore && (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowAll(true)}
            >
              Show all {countries.length} countries
            </Button>
          </Box>
        )}

        {search && displayedCountries.length === 0 && (
          <Typography sx={{ textAlign: "center", color: "text.secondary", p: 4 }}>
            No country found
          </Typography>
        )}
      </Box>
    </Box>
  );
}